<script lang="ts">
/**
 * 扫码能力bridge，对应文档地址：
 * [yoda文档](https://yoda.corp.kuaishou.com/docs/bridge/?kpn=KUAISHOU&namespace=tool&name=scanCode)
 */
export default {
    name: 'BaseScan',
};
</script>

<script setup lang="ts">
import { invoke } from '@yoda/bridge';
import type { ScanCodeParams } from '@yoda/bridge-types';
import { computed } from 'vue-demi';

import { compareVersion, uaGetInfo, isIOS } from '@pet/base.utils';

export type ScanScene = ScanCodeParams['scene'];

export interface ScanConfig {
    isNeedCallBack?: boolean;
    isForceCloseScanPage?: boolean;
    targetHandlerActionList?: string[];
}

export interface ScanResult {
    extendInfo?: {
        kwaiUrl: string;
    };
    originQRCode?: string;
    errorMessage?: any;
}

export type OpenTarget = '_self' | '_blank';

interface Props {
    /**
     * 透传的scan入参
     * 现在看来只能传**targetHandlerActionList**
     */
    config?: ScanConfig;
    /**
     * 如果直接想简单的打开一个页面可以配置
     */
    target?: OpenTarget;
    /**
     * 扫描出错时的兜底文案
     */
    errMsg?: string;
    /**
     * 以前的扫码场景
     */
    scene?: ScanScene;
}

const props = withDefaults(defineProps<Props>(), {
    errMsg: '暂时无法识别哦',
    scene: 'normal',
});

const emits = defineEmits<{
    (event: 'result', val: ScanResult): void;
    (event: 'click'): void;
}>();

function isNeedDefaultSceneApp() {
    const { kwai, ksnebula } = uaGetInfo();
    const appVersion = kwai ?? ksnebula ?? '';
    // 开始支持的最小版本
    // iOS10.10.40开始 安卓10.11.20开始
    const startSupportedApp = isIOS() ? '10.10.40' : '10.11.20';
    const result = compareVersion(appVersion, startSupportedApp);
    return result === 'lt';
}

const scanParams = computed(() => {
    // 默认扫码以及版本兜底
    if (props.config === undefined || isNeedDefaultSceneApp()) {
        return {
            scene: props.scene,
        };
    }

    const defaultConfig = {
        isNeedCallBack: true,
        isForceCloseScanPage: true,
    };
    return {
        ...defaultConfig,
        ...props.config,
    };
});

async function scanAction(url?: string) {
    if (url === undefined) {
        console.error('scan:', 'unavailable url');
        return;
    }
    if (props.target === '_self') {
        window.location.replace(url);
    } else if (props.target === '_blank') {
        const startWithHttp = url.startsWith('http');
        if (startWithHttp) {
            await invoke('tool.loadUrlOnNewPage', { url, type: 'back' });
        } else {
            window.location.href = url;
        }
    }
}

async function handleClick() {
    emits('click');
    try {
        const res = (await invoke('tool.scanCode', scanParams.value)) as unknown as ScanResult;
        emits('result', res);
        const url = res.extendInfo?.kwaiUrl;
        scanAction(url);
    } catch (error) {
        emits('result', {
            errorMessage: error,
        });
        if (Boolean(props.errMsg)) {
            await invoke('ui.showToast', {
                type: 'normal',
                text: props.errMsg,
            });
        }
    }
}
</script>

<template>
    <span role="button" @click="handleClick"><slot /></span>
</template>

<style lang="scss" scoped></style>
